import { ApiError } from '../identity/types';
import { MOCK_JURISDICTIONS } from '../tax-settings/mockJurisdictionCatalog';
import { computeMockTaxSetup, type MockRequestState } from '../tax-settings/mockTaxSetupStatus';
import type {
  HistoricalW4Dto,
  JurisdictionValueDto,
  MultiStateDto,
  ReciprocityDto,
  SaveFederalW4Input,
  SaveStateTaxInput,
  StateDraftDto,
  StateOptionDto,
  TaxSetupResponse,
  FederalDraftDto,
  AdditionalWithholdingDto,
} from './types';

/**
 * Synthetic, in-memory stand-in for GET/PUT /api/me/employee/taxes/**. It mirrors only the OBSERVABLE rules of the real endpoint so the
 * screens can be exercised in a browser: derived states are never input, a state without a wage income tax takes no filing status,
 * reciprocity is a request that stays pending until an administrator reviews it (there is no admin in the mock, so it stays pending),
 * and nothing is ever "submitted" or calculated. No real person, company or certificate content exists here.
 */
export interface MockTaxFacts {
  work: string | null;
  residence: string | null;
  historical: HistoricalW4Dto | null;
  /** States of other locations the employee is allowed to work at (assignment is not proof wages were earned there). */
  additional?: string[];
  /** True when the employee-safe work-state lookup could not be reached (the work state is then Unknown with an honest note). */
  lookupDown?: boolean;
  /** A reciprocity request that already exists when the employee opens the screen (a reviewer's decision; there is no administrator in the mock). */
  initialRequest?: { status: 'SubmittedPendingReview' | 'Approved' | 'Revoked' | 'Rejected'; effectiveFrom: string | null; expiresOn: string | null };
}

const GENERIC_FILING_STATUSES = ['Single', 'Married', 'Head of Household'];
const AGREEMENTS: Record<string, { form: string; description: string }> = {
  'NJ|PA': { form: 'NJ-165', description: 'Certificate of nonresidence in New Jersey, completed by the Pennsylvania resident and given to the New Jersey employer.' },
  'PA|NJ': { form: 'REV-419', description: 'Nonresidency exemption certification, completed by the New Jersey resident and given to the Pennsylvania employer.' },
};
const FEDERAL_STATUS_NAMES: Record<number, string> = { 3: 'SingleOrMarriedFilingSeparately', 4: 'MarriedFilingJointly', 5: 'HeadOfHousehold' };
const MAX_AMOUNT = 9_999_999.99;

const refused = (code: string, message: string) => new ApiError(400, message, null, false, code);
const money = (v: number | undefined, label: string) => {
  if (v === undefined) return;
  if (v < 0 || v > MAX_AMOUNT || Math.round(v * 100) / 100 !== v) throw new ApiError(400, `${label} must be zero or more, with at most two decimal places.`);
};
const nameOf = (code: string | null) => (code ? (MOCK_JURISDICTIONS.get(code)?.name ?? code) : null);
/** Like the server: a code that is not in the shared jurisdiction catalog is treated as not known (never shown or offered). */
const known = (code: string | null): string | null => (code && MOCK_JURISDICTIONS.has(code) ? code : null);
const todayIso = () => new Date().toISOString().slice(0, 10);
const us = (d: string | null | undefined) => {
  const m = d ? /^(\d{4})-(\d{2})-(\d{2})/.exec(d) : null;
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

type MockRequest = { status: 'SubmittedPendingReview' | 'Withdrawn' | 'Approved' | 'Revoked' | 'Rejected'; at: string; effectiveFrom?: string | null; expiresOn?: string | null };

export class MockTaxBackend {
  private federal: FederalDraftDto | null = null;
  private state: StateDraftDto | null = null;
  private additional: AdditionalWithholdingDto = { state: null, value: null, type: null };
  private request: MockRequest | null = null;
  private requestSeeded = false;

  constructor(private readonly facts: () => MockTaxFacts) {}

  private factsKnown(): MockTaxFacts {
    const f = this.facts();
    return { ...f, work: known(f.work), residence: known(f.residence), additional: (f.additional ?? []).filter((c) => known(c) && c !== f.work && c !== f.residence) };
  }

  /** EPN-52 PR 5: the employee's derived work and residence state codes (known jurisdictions only), in the server's order. */
  derivedStateCodes(): { code: string; source: 'Work' | 'AdditionalWork' | 'Residence' }[] {
    const f = this.factsKnown();
    const list: { code: string; source: 'Work' | 'AdditionalWork' | 'Residence' }[] = [];
    const add = (code: string | null, source: 'Work' | 'AdditionalWork' | 'Residence') => {
      if (code && !list.some((o) => o.code === code)) list.push({ code, source });
    };
    add(f.work, 'Work');
    (f.additional ?? []).forEach((c) => add(c, 'AdditionalWork'));
    add(f.residence, 'Residence');
    return list;
  }

  private options(): StateOptionDto[] {
    const f = this.factsKnown();
    const list: StateOptionDto[] = [];
    const add = (code: string | null, source: 'Work' | 'Residence') => {
      if (!code || list.some((o) => o.code === code)) return;
      const entry = MOCK_JURISDICTIONS.get(code)!;
      // A territory has no state-election form in EPN: no filing statuses and no allowances (the generic statuses are never offered for it).
      if (entry.type === 'Territory') {
        list.push({ code, name: entry.name, source, ruleKind: 'NotYetSupported', filingStatuses: [], usesAllowances: false });
        return;
      }
      const noTax = entry.noWageIncomeTax === true;
      list.push({ code, name: entry.name, source, ruleKind: noTax ? 'NoWageIncomeTax' : 'PendingSmeConfiguration', filingStatuses: noTax ? [] : GENERIC_FILING_STATUSES, usesAllowances: !noTax });
    };
    add(f.work, 'Work');
    (f.additional ?? []).forEach((c) => add(c, 'Work'));
    add(f.residence, 'Residence');
    return list;
  }

  async saveFederal(input: SaveFederalW4Input): Promise<void> {
    if (!FEDERAL_STATUS_NAMES[input.filingStatus]) throw new ApiError(400, 'Please choose a filing status that belongs to the 2020 or later W-4.');
    money(input.dependentsAmount, 'Dependent amount');
    money(input.otherIncome, 'Other income');
    money(input.deductions, 'Deductions');
    money(input.extraWithholding, 'Extra withholding');
    money(input.mostRecentWithholdingAmount, 'Most recent withholding amount');
    this.federal = {
      filingStatus: FEDERAL_STATUS_NAMES[input.filingStatus]!,
      multipleJobs: input.multipleJobs,
      dependentsAmount: input.dependentsAmount,
      otherIncome: input.otherIncome,
      deductions: input.deductions,
      extraWithholding: input.extraWithholding,
      mostRecentWithholdingAmount: input.mostRecentWithholdingAmount ?? null,
    };
  }

  async saveState(input: SaveStateTaxInput): Promise<void> {
    const option = this.options().find((o) => o.code === input.stateCode);
    if (!option) throw refused('state_not_derived', 'That state is not one of the states set from your work and residence addresses.');
    if (option.ruleKind === 'NotYetSupported') throw refused('JURISDICTION_NOT_SUPPORTED', `EPN has no state withholding form for ${option.name} yet, so nothing can be saved for it.`);
    if (option.ruleKind === 'NoWageIncomeTax') {
      if (input.filingStatus || input.allowances !== undefined) throw new ApiError(400, `${option.name} has no state wage income tax withholding form. Filing status and allowances do not apply.`);
    } else {
      if (!input.filingStatus || !option.filingStatuses.includes(input.filingStatus)) throw new ApiError(400, 'Please choose a filing status.');
      if (input.allowances === undefined || input.allowances < 0 || input.allowances > 99) throw new ApiError(400, 'Total allowances must be a whole number from 0 to 99.');
    }
    money(input.mostRecentWithholdingAmount, 'Most recent withholding amount');
    if (input.additionalWithholdingState || input.additionalWithholdingValue !== undefined || input.additionalWithholdingType) {
      if (!input.additionalWithholdingState || input.additionalWithholdingValue === undefined || !input.additionalWithholdingType) {
        throw new ApiError(400, 'Additional state withholding needs a state, a value and a type.');
      }
      money(input.additionalWithholdingValue, 'Additional withholding value');
    }
    this.state = {
      stateCode: input.stateCode,
      filingStatus: input.filingStatus ?? null,
      allowances: input.allowances ?? null,
      mostRecentWithholdingAmount: input.mostRecentWithholdingAmount ?? null,
    };
    this.additional = {
      state: input.additionalWithholdingState ?? null,
      value: input.additionalWithholdingValue ?? null,
      type: input.additionalWithholdingType ?? null,
    };
  }

  async clear(section: 'federal' | 'state'): Promise<void> {
    if (section === 'federal') this.federal = null;
    else {
      this.state = null;
      this.additional = { state: null, value: null, type: null };
    }
  }

  /** Starts from the scenario's existing request (a reviewer's decision), once: the mock has no administrator, so the employee's own actions are all that change it. */
  private seedRequest(): void {
    if (this.requestSeeded) return;
    this.requestSeeded = true;
    const initial = this.facts().initialRequest;
    if (initial) this.request = { status: initial.status, at: '2026-09-02T15:30:00Z', effectiveFrom: initial.effectiveFrom, expiresOn: initial.expiresOn };
  }

  async submitCertificate(file: File): Promise<void> {
    const r = this.reciprocity();
    if (r.status === 'SubmittedPendingReview') throw refused('reciprocity_already_pending', 'Your certificate is already waiting for review.');
    if (!r.canSubmit) throw refused('reciprocity_not_eligible', 'A reciprocity exemption is not available for your work and residence states.');
    if (!/\.(pdf|png|jpe?g)$/i.test(file.name)) throw new ApiError(400, 'Allowed types: PDF, PNG, JPEG.');
    if (file.size <= 0) throw new ApiError(400, 'The file is empty.');
    if (file.size > 10 * 1024 * 1024) throw new ApiError(400, 'The file exceeds the 10 MB limit.');
    this.request = { status: 'SubmittedPendingReview', at: '2026-10-18T15:30:00Z' };
  }

  private requestState(): MockRequestState | null {
    this.seedRequest();
    const r = this.request;
    return r ? (r.status === 'SubmittedPendingReview' ? 'Pending' : r.status) : null;
  }

  async withdraw(): Promise<void> {
    if (this.request?.status === 'SubmittedPendingReview') this.request = { status: 'Withdrawn', at: this.request.at };
  }

  private reciprocity(): ReciprocityDto {
    this.seedRequest();
    const { work, residence } = this.factsKnown();
    const base = { workState: work, residenceState: residence, requiredForm: null, requiredFormDescription: null, effectiveFrom: null, submittedAtUtc: null, reviewNote: null, canSubmit: false, canWithdraw: false };
    if (!work || !residence) return { ...base, status: 'Unknown', reason: 'Your work state or residence state is not available yet, so reciprocity cannot be checked. Your payroll administrator will review this.' };
    if (work === residence) return { ...base, status: 'NotApplicable', reason: 'Reciprocity only applies when you live in a different state from the state you work in.' };
    const agreement = AGREEMENTS[`${work}|${residence}`];
    if (!agreement) return { ...base, status: 'NotApplicable', reason: `No reciprocal agreement is on file between ${nameOf(residence)} and ${nameOf(work)}. A payroll tax specialist confirms this list.` };
    const withAgreement = { ...base, requiredForm: agreement.form, requiredFormDescription: agreement.description };
    const request = this.request;
    if (request?.status === 'SubmittedPendingReview') {
      return { ...withAgreement, status: 'SubmittedPendingReview', reason: 'Your certificate is waiting for your payroll administrator to review. Your withholding has not changed.', submittedAtUtc: request.at, canWithdraw: true };
    }
    if (request?.status === 'Approved') {
      const submitted = { submittedAtUtc: request.at, effectiveFrom: request.effectiveFrom ?? null, expiresOn: request.expiresOn ?? null };
      if (request.expiresOn && request.expiresOn <= todayIso()) {
        return { ...withAgreement, ...submitted, status: 'Expired', reason: `Your reciprocity approval expired on ${us(request.expiresOn)} and no longer applies. Send a new certificate to renew it. Your withholding has not changed.`, canSubmit: true };
      }
      const until = request.expiresOn ? ` until ${us(request.expiresOn)}` : ', with no expiry date recorded';
      return { ...withAgreement, ...submitted, status: 'Approved', reason: `Your payroll administrator approved this request, effective ${us(request.effectiveFrom)}${until}. This does not change your withholding yet. Send a new certificate when it expires.` };
    }
    if (request?.status === 'Revoked') {
      return { ...withAgreement, status: 'Revoked', reason: 'Your payroll administrator ended this approval. If you still qualify, send a new certificate. Your withholding has not changed.', submittedAtUtc: request.at, canSubmit: true };
    }
    if (request?.status === 'Rejected') {
      return { ...withAgreement, status: 'Rejected', reason: 'Your certificate was not accepted. You can send a new one. Your withholding has not changed.', submittedAtUtc: request.at, reviewNote: 'The form is not signed.', canSubmit: true };
    }
    return { ...withAgreement, status: 'EligibleCertificateNeeded', reason: `You may qualify. Submit a completed ${agreement.form} for review. This is a request, not an exemption: your payroll administrator must approve it first.`, canSubmit: true };
  }

  build(): TaxSetupResponse {
    const f = this.factsKnown();
    const options = this.options();
    const value = (code: string | null, note: string, unknownNote: string): JurisdictionValueDto =>
      code ? { value: nameOf(code), status: 'Verified', note } : { value: null, status: 'Unknown', note: unknownNote };
    const workState = value(
      f.work,
      'From your employer-assigned primary work location.',
      f.lookupDown
        ? 'Your work state comes from the work location your employer assigned. It could not be looked up right now.'
        : 'Your employer has not assigned a primary work location yet.',
    );
    const residenceState = value(f.residence, 'From your Home/Residence Address.', 'Your residence state comes from your Home/Residence Address in Employee Setup.');
    const local: JurisdictionValueDto = { value: null, status: 'Unknown', note: 'Local tax jurisdictions are not available yet. They need the verified work-location and address tax mapping.' };
    const reciprocity = this.reciprocity();

    const unresolved = ['Wage allocation by work location needs recorded hours per location, which are not available yet.'];
    const extra = f.additional ?? [];
    const extraNames = extra.map((c) => nameOf(c)!);
    let multi: MultiStateDto;
    if (f.work && f.residence && f.work !== f.residence) {
      const also = extra.length > 0 ? ` You are also assigned to work locations in ${extraNames.join(' and ')}.` : '';
      multi = {
        status: 'ReviewRequired',
        states: [nameOf(f.work)!, nameOf(f.residence)!, ...extraNames],
        message: `Multi-State Employee: you work in ${nameOf(f.work)} and live in ${nameOf(f.residence)}.${also} Review withholding setup before payroll processing.`,
        unresolved,
      };
    } else if (extra.length > 0 && (f.work ?? f.residence)) {
      const states = [nameOf((f.work ?? f.residence)!)!, ...extraNames];
      multi = { status: 'ReviewRequired', states, message: `Multi-State Employee: you are assigned to work locations in ${states.join(' and ')}. Review withholding setup before payroll processing.`, unresolved };
    } else if (!f.work || !f.residence) {
      multi = { status: 'Unresolved', states: [], message: 'A state is not available yet, so multi-state withholding cannot be checked. Your payroll administrator will review this.', unresolved };
    } else {
      multi = { status: 'NotApplicable', states: [nameOf(f.work)!], message: 'Your work and residence are in the same state.', unresolved: [] };
    }

    const reciprocityLabel: Record<string, string> = {
      NotApplicable: 'Not applicable', Unknown: 'Unknown', EligibleCertificateNeeded: 'Eligible. Certificate needed',
      SubmittedPendingReview: 'Submitted. Pending review', Approved: 'Approved', Rejected: 'Rejected. Please resubmit', NeedsReview: 'Needs review (states changed)',
      Expired: 'Expired. Please renew', Revoked: 'Revoked. Please resubmit',
    };
    const federalLabel = f.historical && !this.federal
      ? 'On file: 2019 or earlier W-4 (historical record)'
      : this.federal ? 'W-4 answers saved as a draft. Not submitted: the signature step is not available yet.' : 'Not started';

    return {
      federal: { status: this.federal ? 'DraftNotSubmitted' : 'NotStarted', draft: this.federal, historical: f.historical, signatureCapabilityAvailable: false },
      state: {
        status: this.state ? 'DraftNotSubmitted' : 'NotStarted',
        options,
        draft: this.state,
        ruleNote: options.length === 0
          ? 'Your state is set from your work and residence addresses. It is not available yet.'
          : options.some((o) => o.ruleKind === 'NotYetSupported')
            ? 'EPN has no state withholding form for a U.S. territory yet. Nothing is saved for it, and payroll tax is not calculated for it. Your payroll administrator will review this.'
            : options.some((o) => o.ruleKind === 'PendingSmeConfiguration')
            ? 'State withholding forms differ by state. These answers are saved as a draft until a payroll tax specialist confirms the form for your state.'
            : null,
      },
      settings: {
        workState, workLocal: local, residenceState, residenceLocal: local,
        additionalWithholding: this.additional,
        sui: { status: 'NotSet', tooltip: 'Set by payroll administrator after state unemployment review' },
        reciprocity,
      },
      summary: {
        federal: federalLabel, workState, residenceState, reciprocityLabel: reciprocityLabel[reciprocity.status] ?? 'Unknown', workLocal: local, residenceLocal: local,
        sui: { value: null, status: 'PendingReview', note: 'Set by your payroll administrator after state unemployment review.' },
        sdiPfl: { value: null, status: 'Unknown', note: 'State disability / paid family leave applicability is not available yet.' },
        multiState: multi,
        payrollReady: false,
        payrollReadinessNote: 'This is not a tax calculation or a readiness check. Payroll uses your administrator\'s confirmed tax setup, which is reviewed separately.',
      },
      referenceDataVersion: '2026-10-18-draft-1',
      referenceDataStatus: 'DraftPendingSmeApproval',
      taxSetup: computeMockTaxSetup({
        view: 'employee',
        workState: f.work,
        workStateReason: f.lookupDown ? 'LookupUnavailable' : 'NotAssigned',
        residenceState: f.residence,
        secondaryStates: f.additional ?? [],
        hasStateDraft: this.state !== null,
        agreementDocumented: !!f.work && !!f.residence && !!AGREEMENTS[`${f.work}|${f.residence}`],
        request: this.request ? { status: this.requestState() ?? 'Pending', effectiveFrom: this.request.effectiveFrom ?? null, expiresOn: this.request.expiresOn ?? null } : null,
      }),
    };
  }
}
